import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { type AuthTokens, CognitoClient } from './cognito.client';

export interface AuthenticatedProfile {
  readonly email: string;
  readonly fullName: string | undefined;
  readonly groups: readonly string[];
}

/**
 * Les jetons vivent en `sessionStorage` : ils disparaissent à la fermeture de
 * l'onglet, ce qui réduit la fenêtre d'exposition en cas de faille XSS par
 * rapport à `localStorage`. Le prix à payer est qu'il faut se reconnecter en
 * ouvrant un nouvel onglet — c'est un compromis délibéré, et le lien par e-mail
 * le rend supportable.
 */
const STORAGE_KEY = 'champtable.session';

/** Marge avant expiration déclenchant le renouvellement silencieux. */
const RENEW_MARGIN_MS = 5 * 60 * 1000;

/**
 * Session de l'utilisateur : jetons, profil, et renouvellement silencieux.
 *
 * Les parcours d'authentification eux-mêmes vivent dans les écrans, qui
 * appellent `CognitoClient` puis remettent les jetons obtenus ici via `adopt`.
 * Ce service ne connaît donc ni mot de passe, ni code, ni lien : uniquement
 * l'état de la session.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly cognito = inject(CognitoClient);
  private readonly tokens = signal<AuthTokens | undefined>(undefined);
  private renewTimer: ReturnType<typeof setTimeout> | undefined;

  readonly isAuthenticated = computed(() => {
    const current = this.tokens();
    return current !== undefined && current.expiresAt > Date.now();
  });

  readonly profile = computed<AuthenticatedProfile | undefined>(() => {
    const current = this.tokens();
    if (!current) {
      return undefined;
    }
    const claims = decodeClaims(current.idToken);
    const groups = claims['cognito:groups'];
    return {
      email: typeof claims['email'] === 'string' ? claims['email'] : '',
      fullName: typeof claims['name'] === 'string' ? claims['name'] : undefined,
      groups: Array.isArray(groups) ? groups.map(String) : [],
    };
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cancelRenew());
  }

  /**
   * Restaure la session au démarrage de l'application.
   *
   * Un jeton d'accès expiré n'est pas rédhibitoire : le jeton de
   * rafraîchissement vaut trente jours, et c'est lui qui évite de redemander une
   * connexion à chaque rechargement de page.
   */
  async restoreSession(): Promise<void> {
    const stored = readStoredTokens();
    if (!stored) {
      return;
    }

    if (stored.expiresAt > Date.now() + RENEW_MARGIN_MS) {
      this.adopt(stored);
      return;
    }

    if (!stored.refreshToken) {
      this.clear();
      return;
    }

    try {
      const renewed = await this.cognito.refresh(stored.refreshToken);
      // Cognito ne renvoie pas le jeton de rafraîchissement : on conserve le nôtre.
      this.adopt({ ...renewed, refreshToken: renewed.refreshToken ?? stored.refreshToken });
    } catch {
      this.clear();
    }
  }

  /** Enregistre les jetons issus d'un parcours d'authentification réussi. */
  adopt(tokens: AuthTokens): void {
    this.tokens.set(tokens);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    this.scheduleRenew(tokens);
  }

  async signOut(): Promise<void> {
    const refreshToken = this.tokens()?.refreshToken;
    this.clear();

    if (refreshToken) {
      // Sans cette révocation, effacer les jetons du navigateur laisserait une
      // session utilisable pendant trente jours à qui aurait pu les recopier.
      // Un échec ne doit pas empêcher la déconnexion locale d'aboutir.
      await this.cognito.revoke(refreshToken).catch(() => undefined);
    }
  }

  /**
   * Jeton présenté à l'API.
   *
   * On envoie le jeton d'accès et non le jeton d'identité : c'est celui destiné
   * à autoriser des appels. L'autorisation JWT d'API Gateway le valide en
   * comparant la revendication `client_id` à l'audience configurée.
   */
  accessToken(): string | undefined {
    const current = this.tokens();
    return current && current.expiresAt > Date.now() ? current.accessToken : undefined;
  }

  private clear(): void {
    this.cancelRenew();
    this.tokens.set(undefined);
    sessionStorage.removeItem(STORAGE_KEY);
  }

  private scheduleRenew(tokens: AuthTokens): void {
    this.cancelRenew();
    if (!tokens.refreshToken) {
      return;
    }

    const delay = Math.max(tokens.expiresAt - Date.now() - RENEW_MARGIN_MS, 1000);
    this.renewTimer = setTimeout(() => {
      void this.renew(tokens.refreshToken as string);
    }, delay);
  }

  private async renew(refreshToken: string): Promise<void> {
    try {
      const renewed = await this.cognito.refresh(refreshToken);
      this.adopt({ ...renewed, refreshToken: renewed.refreshToken ?? refreshToken });
    } catch {
      this.clear();
    }
  }

  private cancelRenew(): void {
    if (this.renewTimer !== undefined) {
      clearTimeout(this.renewTimer);
      this.renewTimer = undefined;
    }
  }
}

function readStoredTokens(): AuthTokens | undefined {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<AuthTokens>;
    if (typeof parsed.accessToken !== 'string' || typeof parsed.idToken !== 'string') {
      return undefined;
    }
    return {
      accessToken: parsed.accessToken,
      idToken: parsed.idToken,
      refreshToken: typeof parsed.refreshToken === 'string' ? parsed.refreshToken : undefined,
      expiresAt: typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0,
    };
  } catch {
    return undefined;
  }
}

/**
 * Lit les revendications du jeton d'identité, sans vérifier sa signature.
 *
 * Ce n'est pas un oubli : le jeton vient d'un appel TLS à Cognito, et il ne sert
 * ici qu'à afficher un nom et une adresse. Toute décision d'autorisation est
 * prise par l'API, qui vérifie la signature du jeton d'accès. Valider une
 * signature dans le navigateur ne protégerait de rien — qui peut modifier le
 * stockage local peut aussi modifier le code qui le contrôle.
 */
function decodeClaims(idToken: string): Record<string, unknown> {
  const payload = idToken.split('.')[1];
  if (!payload) {
    return {};
  }
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    // Les jetons sont en UTF-8 : sans cette conversion, un nom accentué serait
    // affiché en caractères de contrôle.
    const text = decodeURIComponent(
      json
        .split('')
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return {};
  }
}
